import type { ErrorResponse } from "@types";
import axios, { AxiosRequestHeaders } from "axios";
import auth from "../api/auth";

import {
  COOKIE_ACCESS_TOKEN_KEY,
  COOKIE_FRONT_TOKEN_KEY,
  COOKIE_LOCALE_KEY,
  COOKIE_REFRESH_TOKEN_KEY,
  COOKIE_SHOPPING_CART,
} from "@utils/constants";
import { getContext, getCookie, getLocale } from "@utils/Cookies";
import { isServer } from "@utils/helpers";

const client = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL_V2,
  responseType: "json",
  withCredentials: true,
});

client.defaults.timeout = 120000;

let interceptorId = -1;

let refreshTokenPromise: Promise<string> | null = null;
let redirecting = false;

// const refreshToken = Cookies.get(COOKIE_REFRESH_TOKEN_KEY);
const reGetToken = async (refreshToken: string): Promise<string> => {
  const res = await auth.refresh({ refreshToken }).fetch();
  const token = res.result.accessToken || "";
  const Cookies = await getCookie();
  Cookies.set(COOKIE_ACCESS_TOKEN_KEY, token, {}, 365);
  Cookies.set(COOKIE_FRONT_TOKEN_KEY, token, {}, 365);

  return token;
};

client.interceptors.request.use(
  async function (config) {
    const Cookies = getCookie();
    const token = Cookies.get(COOKIE_ACCESS_TOKEN_KEY);

    if (!config.headers) config.headers = {} as AxiosRequestHeaders;
    if (!config.headers["Accept-Language"])
      config.headers["Accept-Language"] =
        getLocale() || Cookies.get(COOKIE_LOCALE_KEY);
    config.headers["Authorization"] = token ? `Bearer ${token}` : "";
    config.headers[
      process.env.NODE_ENV === "development" ? "Checknext" : "Nextjs"
    ] = true;
    if (!isServer()) {
      config.headers["Current-Url"] =
        // getBasePath([]) + window.location.pathname;
        "https://faradars.org" + window.location.pathname;
    } else {
      const ctx = getContext();
      if (ctx?.req) {
        config.headers["Current-Url"] =
          "https://faradars.org" + ctx.req.url?.replace("?ssr=1", "");
      }
    }
    if (isServer()) {
      const ctx = getContext(); // Assuming req is passed in
      const shoppingCartCookie = ctx?.req?.headers.cookie
        ?.split(";")
        .map((cookie) => cookie.split("=").map((part) => part.trim()))
        .find(([key]) => key === COOKIE_SHOPPING_CART)?.[1];

      config.headers["cart-token"] = shoppingCartCookie || "";
    } else {
      const shoppingCart = Cookies.get(COOKIE_SHOPPING_CART);

      config.headers["cart-token"] = shoppingCart;
    }
    return config;
  },
  function (error) {
    console.log(error, "error");
    return Promise.reject(error);
  },
);
function checkRedirect(response: any, rejectOnClient = false) {
  // const notRedirect = pathName.includes("/webinars") || pathName.includes("/audiobooks") || pathName.includes("/blinds")
  const url = response?.headers["x-redirect-url"];
  // if (url && !notRedirect) {
  if (url) {
    if (!redirecting) {
      if (!isServer()) {
        const params = window.location.search;
        window.location.replace(url + params);
        redirecting = true;
        if (rejectOnClient) return { data: url, code: 307 };
      } else {
        return { data: url, code: 307 };
      }
    }
  }
  return false;
}
client.interceptors.response.use(
  async function (response) {
    const res = checkRedirect(response);
    if (res) {
      return Promise.reject(res);
    }
    return response;
  },
  async function (error) {
    const res = checkRedirect(error.response, true);
    if (res) {
      return Promise.reject(res);
    }
    return Promise.reject(error);
  },
);

export function setClientAuthInterceptorCallbak(callback?: () => void) {
  if (isServer() && interceptorId >= 0) return;
  if (interceptorId >= 0) client.interceptors.response.eject(interceptorId);

  interceptorId = client.interceptors.response.use(
    function (response) {
      return response;
    },
    async function (error) {
      const originalRequest = error.config;
      const Cookies = await getCookie();
      if (error?.response?.status === 401) {
        const refreshToken = Cookies.get(COOKIE_REFRESH_TOKEN_KEY);
        if (refreshToken) {
          if (!refreshTokenPromise) {
            refreshTokenPromise = reGetToken(refreshToken);
          }
          try {
            const accessToken = await refreshTokenPromise;
            client.defaults.headers.common["Authorization"] =
              "Bearer " + accessToken;
            return client(originalRequest);
          } catch (error) {
            const err = error as ErrorResponse;
            if (err.error.code === 403 || err.error.code === 401) {
              Cookies.remove(COOKIE_REFRESH_TOKEN_KEY);
            }
            return Promise.reject(err.error.details ? err.error.details : err);
          } finally {
            refreshTokenPromise = null;
            callback?.();
          }
        } else callback?.();
      }
      return Promise.reject(error);
    },
  );
}

setClientAuthInterceptorCallbak();

export default client;
